// src/hooks/useIncomingArrivals.js
// Watches for incoming faxes that are arriving right now, for the
// "Receiving…" rows on the Incoming tab. Unlike outgoing faxes there is no
// known ID to follow, so while the tab is open it asks the backend every
// 10 seconds (pausing while the browser tab is hidden). When a fax that
// was arriving disappears from the list, it has finished — onFinished is
// called with its ID so the page can refresh and announce it.
import { useEffect, useRef, useState } from 'react';
import { fetchIncomingInProgress } from '../utils/api';

export const ARRIVALS_POLL_MS = 10 * 1000;
const MIN_GAP_MS = 5 * 1000;   // returning to the tab never checks more often than this

export function useIncomingArrivals({ enabled, getToken, onFinished }) {
  const [arriving, setArriving] = useState([]);
  const prevRef     = useRef(new Set());
  const finishedRef = useRef(onFinished);
  const tokenRef    = useRef(getToken);
  const lastRef     = useRef(0);
  finishedRef.current = onFinished;
  tokenRef.current    = getToken;

  useEffect(() => {
    if (!enabled) { setArriving([]); prevRef.current = new Set(); return; }
    let stopped  = false;
    let inFlight = false;

    async function tick() {
      if (stopped || document.hidden || inFlight) return;
      if (Date.now() - lastRef.current < MIN_GAP_MS) return;
      inFlight = true;
      lastRef.current = Date.now();
      try {
        const list = await fetchIncomingInProgress(() => tokenRef.current());
        if (stopped) return;
        const now  = new Set(list.map(a => a.faxId));
        const done = [...prevRef.current].filter(id => !now.has(id));
        prevRef.current = now;
        setArriving(list);
        if (done.length) finishedRef.current?.(done);
      } catch {
        // Transient errors: keep showing what we had, try again next tick
      } finally {
        inFlight = false;
      }
    }

    function onVisible() { if (!document.hidden) tick(); }

    tick();
    const timer = setInterval(tick, ARRIVALS_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);

  return arriving;
}
